import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { format } from 'date-fns';
import { UpdateTag } from '../api/updateTag';
import { taskIdTag } from '../helpers/taskHelpers';
import { MoveTask } from '../api/moveTask';
import { DateTime } from '../../shared/types/util/dateTime';
import { Absence } from '../types/absence';
import { UpdateAbsence } from '../api/updateAbsence';
import { Organization } from '../../shared/types/organization';
import { addAuthHeaders } from '../../shared/api';
import { NewTask } from '../api/newTask';
import { UpdateTask } from '../api/updateTask';
import { CalendarObservation } from '../types/calendar';
import { Project } from '../types/project';
import { Task } from '../types/task';
import { Worker } from '../types/worker';
import { Loan } from '../types/loan';
import { UpdateLoan } from '../api/updateLoan';
import { Alias } from '../types/alias';
import { Dummy } from '../types/dummy';
import { UpdateProject } from '../api/updateProject';
import { config } from '../../shared/config/worker';
import { Tag } from '../../shared/types/tag';
import { SecurityTraining } from '../../shared/types/securityTraining';
import { Certification } from '../../shared/types/certification';
import { WeekPlan } from '../types/weekPlan';
import { RootState } from '.';
import { absence2Task } from '../helpers/converters';

// Define a service using a base URL and expected endpoints
export const workerApi = createApi({
  reducerPath: 'worker',
  baseQuery: fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'tasks',
    'loans',
    'workers',
    'projects',
    'alias',
    'tags',
    'weekPlans',
    'myCompany',
    'organizations',
  ],
  endpoints: (builder) => ({
    getProject: builder.query<Project, number>({
      query: (id) => `/projects/${id}`,
      providesTags: ['projects'],
    }),
    getWorker: builder.query<Worker, number>({
      query: (id) => `/workers/${id}`,
    }),
    getAllProjects: builder.query<Project[], void|{organizations: number[], includeInternalProduction?: boolean, includeOwnProjects?: boolean }>({
      query: ({
        organizations,
        includeInternalProduction,
        includeOwnProjects,
      } = {
        organizations: [],
        includeInternalProduction: false,
        includeOwnProjects: true,
      }) => ({
        url: '/projects',
        params: {
          organizations: organizations.length > 0 ? organizations.join(',') : undefined,
          includeInternalProduction,
          includeOwnProjects,
        },
      }),
      providesTags: ['projects'],
    }),
    getAllWorkers: builder.query<Worker[], void|{organizations: number[], from?: DateTime, to?: DateTime}>({
      query: ({ organizations, from, to } = { organizations: [] }) => ({
        url: '/workers',
        params: {
          organizations: organizations.length > 0 ? organizations.join(',') : undefined,
          from,
          to,
        },
      }),
      providesTags: ['workers'],
    }),
    putProjectBody: builder.mutation<void, { id: number; data: UpdateProject }>({
      query: ({ id, data }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['projects'],
    }),
    getDummies: builder.query<Dummy[], void>({
      query: () => '/workers/dummies',
    }),
    getAllCertificationsWorker: builder.query<Certification[], number>({
      query: (id) => `/workers/${id}/certifications`,
    }),
    getAllDocumentedSecurityTraining: builder.query<SecurityTraining[], number>({
      query: (id) => `/workers/${id}/securityTraining`,
    }),
    getAllTasks: builder.query<Task[], {from: DateTime, to: DateTime}>({
      query: ({ from, to }) => ({
        url: '/tasks',
        params: {
          from,
          to,
        },
      }),
      providesTags: ['tasks'],
    }),
    getTask: builder.query<Task, string|number>({
      query: (id) => ({
        url: `/tasks/${id}`,
      }),
      providesTags: taskIdTag,
    }),
    postCreateTask: builder.mutation<Task, NewTask>({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body: { ...body, type: undefined },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: newTask } = await queryFulfilled;
          const queries = (getState() as any)[workerApi.reducerPath]?.queries ?? {};
          Object.values(queries).forEach((query: any) => {
            if (query?.endpointName === 'getAllTasks' && query?.status === 'fulfilled') {
              dispatch(workerApi.util.updateQueryData('getAllTasks', query.originalArgs, (draft) => {
                if (!draft.find((t) => t.id === newTask.id)) {
                  draft.push(newTask);
                }
              }));
            }
          });
        } catch { /* mutation failed — leave cache unchanged */ }
      },
      invalidatesTags: (t) => [...taskIdTag(t), 'tasks'],
    }),
    putTasksBody: builder.mutation<Task, { id: string; data: UpdateTask }>({
      query: ({ id, data }) => ({
        url: `/tasks/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (t) => [...taskIdTag(t), 'tasks'],
    }),
    deleteTask: builder.mutation<Task, string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (a, b, t) => [...taskIdTag(t), 'tasks'],
    }),
    deleteAllFutureTasksForProject: builder.mutation<Task[], string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
        params: { deleteAllFutureForProject: true },
      }),
      invalidatesTags: ['tasks'],
    }),
    moveTask: builder.mutation<Task, { id: string; body: MoveTask }>({
      query: ({ id, body }) => ({
        url: `/tasks/${id}/move`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (t) => [...taskIdTag(t), 'tasks'],
    }),
    getCalendar: builder.query<CalendarObservation[], {from?: string; to?: string} | void>({
      query: (data) => {
        const from = data?.from;
        const to = data?.to;
        return {
          url: '/calendar',
          params: {
            from,
            to,
          },
        };
      },
    }),
    getOrganizations: builder.query<Organization[], void>({
      query: () => '/organizations',
      providesTags: ['organizations'],
    }),
    getMyOrganization: builder.query<Organization, void>({
      query: () => '/organizations/me',
    }),
    postAbsence: builder.mutation<Absence, { workerId: number|string, body: UpdateAbsence}>({
      query: ({ workerId, body }) => ({
        url: `/workers/${workerId}/absences`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: absence } = await queryFulfilled;
          const newTask = absence2Task(absence);
          const queries = (getState() as any)[workerApi.reducerPath]?.queries ?? {};
          Object.values(queries).forEach((query: any) => {
            if (query?.endpointName === 'getAllTasks' && query?.status === 'fulfilled') {
              dispatch(workerApi.util.updateQueryData('getAllTasks', query.originalArgs, (draft) => {
                if (!draft.find((t) => t.id === newTask.id)) {
                  draft.push(newTask);
                }
              }));
            }
          });
        } catch { /* mutation failed — leave cache unchanged */ }
      },
      invalidatesTags: ['tasks'],
    }),
    putAbsence: builder.mutation<Absence, { workerId: number|string, absenceId: string|number, body: UpdateAbsence}>({
      query: ({ workerId, absenceId, body }) => ({
        url: `/workers/${workerId}/absences/${absenceId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['tasks'],
    }),
    deleteAbsence: builder.mutation<Absence, { workerId: number|string, absenceId: string|number}>({
      query: ({ workerId, absenceId }) => ({
        url: `/workers/${workerId}/absences/${absenceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['tasks'],
    }),
    getWorkerLoans: builder.query<Loan[], string|number>({
      query: (workerId) => ({
        url: `/workers/${workerId}/loans`,
      }),
      providesTags: ['loans'],
    }),
    postLoan: builder.mutation<Loan, UpdateLoan>({
      query: (body) => ({
        url: '/loans',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tasks', 'loans'],
    }),
    putLoan: builder.mutation<Loan, { loanId: string, body: UpdateLoan}>({
      query: ({ loanId, body }) => ({
        url: `/loans/${loanId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['tasks', 'loans'],
    }),
    deleteLoan: builder.mutation<Loan, string>({
      query: (loanId) => ({
        url: `/loans/${loanId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['tasks', 'loans'],
    }),
    approveLoan: builder.mutation<Loan, string>({
      query: (loanId) => ({
        url: `/loans/${loanId}/approve`,
        method: 'PUT',
      }),
      // When a worker loan is accepted it can change which workers are visible
      invalidatesTags: ['tasks', 'loans', 'workers'],
    }),
    putFavourite: builder.mutation<void, { id: number, enabled: boolean }>({
      query: ({ id, enabled }) => ({
        url: `/projects/${id}/favourite`,
        method: 'PUT',
        body: { enabled },
      }),
      invalidatesTags: ['projects'],
    }),
    getAlias: builder.query<Alias, string|number>({
      query: (workerId) => ({
        url: `/alias/${workerId}`,
      }),
      providesTags: ['alias'],
    }),
    putAlias: builder.mutation<void, { id: number|string, target: string }>({
      query: ({ id, target }) => ({
        url: `/alias/${id}`,
        method: 'PUT',
        body: `${target}`,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['alias'],
    }),
    deleteAlias: builder.mutation<void, number|string>({
      query: (workerId) => ({
        url: `/alias/${workerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['alias'],
    }),
    putWorkerTag: builder.mutation<Worker, { workerId: number, tagId: number}>({
      query: ({ workerId, tagId }) => ({
        url: `/workers/${workerId}/tag`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tagId,
      }),
      invalidatesTags: ['workers'],
    }),
    getAllTags: builder.query<Tag[], void>({
      query: () => '/tags',
      providesTags: ['tags'],
    }),
    getTag: builder.query<Tag, number>({
      query: (id) => `/tags/${id}`,
      providesTags: ['tags'],
    }),
    postTag: builder.mutation<Tag, UpdateTag>({
      query: (body) => ({
        url: '/tags',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tags'],
    }),
    putTag: builder.mutation<Tag, { id: number, body: UpdateTag}>({
      query: ({ id, body }) => ({
        url: `/tags/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['tags', 'workers'],
    }),
    deleteTag: builder.mutation<void, number>({
      query: (id) => ({
        url: `/tags/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['tags', 'workers'],
    }),
    getWeekPlans: builder.query<
      WeekPlan[],
      { date: DateTime; organizations: number[]; showOnlyPlanned: boolean }
    >({
      query: ({ date, organizations, showOnlyPlanned }) => ({
        url: `/weekPlanner/${date}`,
        params: {
          organizations: organizations.length > 0 ? organizations.join(',') : undefined,
          plannedOnly: showOnlyPlanned,
        },
      }),
      providesTags: ['weekPlans'],
    }),
    updateWeekPlan: builder.mutation<WeekPlan, WeekPlan>({
      query: (weekPlan) => ({
        url: '/weekPlanner',
        method: 'PUT',
        body: weekPlan,
      }),
      onQueryStarted: async (weekPlan, { dispatch, queryFulfilled, getState }) => {
        const state = getState() as RootState;
        const { organizations } = state.leftFilterState;
        const date = new Date(weekPlan.date);
        const formattedDate = format(date, 'yyyy-MM-dd');

        const patchResults = [true, false].map((showOnlyPlanned) => dispatch(
          workerApi.util.updateQueryData(
            'getWeekPlans',
            { date: formattedDate, organizations, showOnlyPlanned },
            (draft) => {
              const index = draft.findIndex(
                (wp) => wp.projectId === weekPlan.projectId && wp.site === weekPlan.site,
              );
              if (index !== -1) {
                // eslint-disable-next-line no-param-reassign
                draft[index] = weekPlan;
              } else {
                draft.push(weekPlan);
              }
            },
          ),
        ));

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetProjectQuery,
  useGetWorkerQuery,
  useGetAllProjectsQuery,
  useGetAllWorkersQuery,
  usePutProjectBodyMutation,
  useGetDummiesQuery,
  useGetAllCertificationsWorkerQuery,
  useGetAllDocumentedSecurityTrainingQuery,
  useGetAllTasksQuery,
  useGetTaskQuery,
  usePostCreateTaskMutation,
  usePutTasksBodyMutation,
  useDeleteTaskMutation,
  useDeleteAllFutureTasksForProjectMutation,
  useMoveTaskMutation,
  useGetCalendarQuery,
  useGetOrganizationsQuery,
  useGetMyOrganizationQuery,
  usePostAbsenceMutation,
  usePutAbsenceMutation,
  useDeleteAbsenceMutation,
  useGetWorkerLoansQuery,
  usePostLoanMutation,
  usePutLoanMutation,
  useDeleteLoanMutation,
  useApproveLoanMutation,
  usePutFavouriteMutation,
  useGetAliasQuery,
  usePutAliasMutation,
  useDeleteAliasMutation,
  usePutWorkerTagMutation,
  useGetAllTagsQuery,
  useGetTagQuery,
  usePostTagMutation,
  usePutTagMutation,
  useDeleteTagMutation,
  useUpdateWeekPlanMutation,
  util,
} = workerApi;

function apiFormatDate(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

function getLatestMonday(date: Date) {
  const day = date.getDay();
  const diff = (day + 6) % 7; // Calculate the difference to the last Monday
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - diff);
}

export function useGetWeekPlansQuery({
  date,
  organizations,
  showOnlyPlanned = false,
}: {
  date: Date;
  organizations: number[];
  showOnlyPlanned: boolean;
}) {
  const latestMonday = getLatestMonday(date);
  // so the query has the same key as the mutation
  return workerApi.useGetWeekPlansQuery(
    {
      date: apiFormatDate(latestMonday),
      organizations,
      showOnlyPlanned,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
}

export default workerApi.reducer;
